import { useId, useState } from "react";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Panel } from "@/components/ui/Panel";
import { Textarea } from "@/components/ui/textarea";
import type { Verdict, VerdictOverrideRequest } from "@patent-studio/api-types";
import { FieldLabel } from "@/components/ui/FieldLabel";
import { Note } from "@/components/ui/Note";
import { StatusPill } from "@/components/ui/StatusPill";
import { VERDICT_LABELS } from "@/lib/verdict-labels";
import { refusalDetail } from "@/services/api-client";

/** A verdict the attorney may rule: every verdict but not assessed. */
export type RuledVerdict = VerdictOverrideRequest["verdict"];

const RULINGS: readonly RuledVerdict[] = [
  "novel",
  "partially_novel",
  "not_novel",
];

/** What a refusal says when the server never answered. */
const UNREACHED = "The request did not reach the server.";

/** What an override does to the assessment, said on the tag and once it is saved. */
const REPLACES = "Your verdict replaces the assistant's assessment.";

/** Says in the row's head that the attorney set the status in place of the analysis's. */
export function OverriddenPill({ ruled }: { ruled: Verdict | null }) {
  if (ruled === null) return null;
  return (
    <StatusPill
      tone="brand"
      plain
      icon={<Check className="h-3 w-3" aria-hidden />}
      title={REPLACES}
      data-testid="verdict-override"
    >
      Overridden
    </StatusPill>
  );
}

/** What the attorney just set, said once the server has taken it. */
export function OverrideFlash({ set }: { set: Verdict | null }) {
  if (set === null) return null;
  return (
    <Note tone="info" data-testid="override-flash">
      Status set to {VERDICT_LABELS[set]}. {REPLACES}
    </Note>
  );
}

/** Saves the attorney's ruling, or takes it back with `null`, with why; resolves once the server has taken it. */
export type OverrideVerdict = (
  positionId: string,
  verdict: RuledVerdict | null,
  reason: string | null,
) => Promise<unknown>;

/**
 * The override's state on one row: whether the box is open, and what the
 * attorney last set, which the flash line says until the box opens again.
 * Taking the override back sets nothing, so it says nothing.
 */
export function useOverride(
  save: (
    verdict: RuledVerdict | null,
    reason: string | null,
  ) => Promise<unknown>,
) {
  const [open, setOpen] = useState(false);
  const [flash, setFlash] = useState<Verdict | null>(null);
  return {
    open,
    flash,
    start: () => {
      setFlash(null);
      setOpen(true);
    },
    close: () => setOpen(false),
    save: (verdict: RuledVerdict | null, reason: string | null) =>
      save(verdict, reason).then(() => setFlash(verdict)),
  };
}

/** Why the attorney ruled the verdict, while the ruling stands. */
export function OverrideReason({ reason }: { reason: string | null }) {
  if (reason === null) return null;
  return (
    <div data-testid="override-reason">
      <MicroLabel as="p" size="sm">
        Reason for the override
      </MicroLabel>
      <p className="text-[12px]">{reason}</p>
    </div>
  );
}

/** The three statuses, the one standing marked current and the one chosen drawn as the act. */
function Choices({
  current,
  choice,
  onChoose,
}: {
  current: Verdict;
  choice: Verdict;
  onChoose: (verdict: Verdict) => void;
}) {
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {RULINGS.map((verdict) => (
        <Button
          key={verdict}
          type="button"
          size="dense"
          variant={verdict === choice ? "primary" : "secondary"}
          onClick={() => onChoose(verdict)}
        >
          {VERDICT_LABELS[verdict]}
          {verdict === current ? " (current)" : ""}
        </Button>
      ))}
    </div>
  );
}

/**
 * The override box: the attorney sets the novelty status in place of the
 * analysis's, and says why. Over a Not novel verdict the reason is required,
 * since the override is the way past it; otherwise it is optional. Choosing
 * the analysis's own verdict again takes the override back. A refusal from
 * the server is said under the box, which stays open.
 */
export function OverrideBox({
  analysis,
  ruled,
  busy,
  onOverride,
  onClose,
}: {
  /** The verdict the analysis serves. */
  analysis: Verdict;
  /** The attorney's standing ruling, or nothing. */
  ruled: Verdict | null;
  busy: boolean;
  onOverride: (
    verdict: RuledVerdict | null,
    reason: string | null,
  ) => Promise<unknown>;
  onClose: () => void;
}) {
  const reasonId = useId();
  const current = ruled ?? analysis;
  const [choice, setChoice] = useState<Verdict>(current);
  const [reason, setReason] = useState("");
  const [refusal, setRefusal] = useState<string | null>(null);
  const takesBack = ruled !== null && choice === analysis;
  const required = analysis === "not_novel" && !takesBack;
  const said = reason.trim();
  // Taking back is a change even when a re-run has come to agree with the ruling.
  const ready = (choice !== current || takesBack) && (!required || said !== "");
  const confirm = () => {
    setRefusal(null);
    const verdict = takesBack ? null : (choice as RuledVerdict);
    onOverride(verdict, takesBack || said === "" ? null : said)
      .then(onClose)
      .catch((failure: unknown) =>
        setRefusal(refusalDetail(failure, UNREACHED)),
      );
  };
  return (
    <Panel data-testid="override-box" shape="row" pad="snug" tone="brand">
      <MicroLabel as="p" size="sm">
        Override the novelty status
      </MicroLabel>
      <Choices current={current} choice={choice} onChoose={setChoice} />
      <div className="mt-3">
        <FieldLabel htmlFor={reasonId} required={required} optional={!required}>
          Reason for the override
        </FieldLabel>
        <Textarea
          id={reasonId}
          rows={2}
          aria-required={required}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Why do you disagree with the assessment? Note the prior art or element scope that informed your decision."
          className="bg-card text-[12px]"
        />
      </div>
      <div className="mt-2 flex justify-end gap-2">
        <Button type="button" size="dense" variant="quiet" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          size="dense"
          disabled={busy || !ready}
          onClick={confirm}
        >
          Confirm
        </Button>
      </div>
      {refusal !== null && (
        <Note tone="destructive" role="alert" className="mt-1">
          Your verdict could not be saved: {refusal}
        </Note>
      )}
    </Panel>
  );
}
